/**
 * Lead capture for qualified identity / Audit requests.
 *
 * Durability + email require server-side config:
 * - Prefer VITE_LEADS_WEBHOOK_URL (n8n/Supabase Edge/etc.) that stores the row
 *   and sends an immediate notification email.
 * - Or VITE_SUPABASE_URL + VITE_SUPABASE_ANON_KEY with a `leads` table
 *   (see supabase/leads.sql). Email still needs a DB webhook or Edge Function.
 *
 * Until those env vars are set, submitLead returns mode "none" and the UI
 * must fall back to mailto. Do not treat leads as validated without config.
 */

import { createAuditHref, type AuditMailtoContext, type ScoreResultPayload } from '../components/scoreResultUrl';
import { viteEnv } from './viteEnv';
export type LeadPayload = {
  name: string;
  email: string;
  note?: string;
  source: 'audit_request';
  score?: ScoreResultPayload | null;
};
export type LeadSubmitResult = {
  ok: true;
  mode: 'webhook' | 'supabase';
} | {
  ok: false;
  mode: 'none' | 'error';
  error?: string;
};
function webhookUrl(): string | undefined {
  return viteEnv('VITE_LEADS_WEBHOOK_URL');
}
function supabaseUrl(): string | undefined {
  return viteEnv('VITE_SUPABASE_URL');
}
function supabaseAnonKey(): string | undefined {
  return viteEnv('VITE_SUPABASE_ANON_KEY');
}

/** True when a durable lead endpoint is configured. */
export function isLeadPlumbingConfigured(): boolean {
  return Boolean(webhookUrl() || supabaseUrl() && supabaseAnonKey());
}
export function mailtoAuditFallback(score: ScoreResultPayload | null, context: AuditMailtoContext = {}): string {
  return createAuditHref(score, {
    ...context,
    pageUrl: context.pageUrl ?? (typeof window !== 'undefined' ? window.location.href : undefined)
  });
}
async function postWebhook(payload: LeadPayload): Promise<boolean> {
  const url = webhookUrl();
  if (!url) return false;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      ...payload,
      submittedAt: new Date().toISOString(),
      page: typeof window !== 'undefined' ? window.location.href : undefined
    })
  });
  return res.ok;
}
async function postSupabase(payload: LeadPayload): Promise<boolean> {
  const url = supabaseUrl();
  const key = supabaseAnonKey();
  if (!url || !key) return false;
  const res = await fetch(`${url.replace(/\/$/, '')}/rest/v1/leads`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      apikey: key,
      Authorization: `Bearer ${key}`,
      Prefer: 'return=minimal'
    },
    body: JSON.stringify({
      name: payload.name,
      email: payload.email,
      note: payload.note ?? null,
      source: payload.source,
      score_total: payload.score?.totalScore ?? null,
      score_band: payload.score?.band ?? null,
      score_payload: payload.score ?? null
    })
  });
  return res.ok;
}
export async function submitLead(payload: LeadPayload): Promise<LeadSubmitResult> {
  if (!isLeadPlumbingConfigured()) {
    return {
      ok: false,
      mode: 'none'
    };
  }
  try {
    if (webhookUrl()) {
      const ok = await postWebhook(payload);
      return ok ? {
        ok: true,
        mode: 'webhook'
      } : {
        ok: false,
        mode: 'error',
        error: 'Webhook rejected the lead.'
      };
    }
    const ok = await postSupabase(payload);
    return ok ? {
      ok: true,
      mode: 'supabase'
    } : {
      ok: false,
      mode: 'error',
      error: 'Supabase rejected the lead.'
    };
  } catch (err) {
    return {
      ok: false,
      mode: 'error',
      error: err instanceof Error ? err.message : 'Lead submit failed.'
    };
  }
}